import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import EntityForm from '../../../../components/form/base/EntityForm';
import { CategoryValues } from '../../model/categoryModel';
import TextField from '../../../../components/form/elements/TextField';
import { TEXT_FIELD_COLOR, TEXT_FIELD_TYPE, TEXT_FIELD_VARIANT } from '../../../../components/form/enum/formProps';
import Title from '../../../../components/Title';
import Breadcrumbs from '../../../../components/Breadcrumbs';
import { BreadcrumbsLinkData } from '../../../../components/Breadcrumbs/model/breadcrumbsModel';
import CategoryFilter from '../../../../components/form/custom/CategoryFilter';
import Image from '../../../../components/form/elements/Image';

interface CategoryFormProps {
  parentId?: number;
  categoryId?: number;
  title: string;
  breadcrumbs: BreadcrumbsLinkData[];
  initialValues: CategoryValues;
  onSubmit: (values: CategoryValues) => void;
  isRootCategory?: boolean;
  isMainCategory?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = (props) => {
  const { t } = useTranslation();
  const validationSchema = useMemo<object>(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .min(2, 'validation.too_short')
          .max(70, 'validation.too_long')
          .required('validation.is_required'),
        categoryOne: Yup.object().shape({
          value: props.isMainCategory
            ? Yup.string().ensure()
            : Yup.string().ensure().required('validation.is_required'),
          key: props.isMainCategory ? Yup.string().ensure() : Yup.string().ensure().required('validation.is_required'),
        }),
        categoryTwo: Yup.object().shape({
          value: Yup.string(),
          key: Yup.string(),
        }),
      }),
    [props.isMainCategory],
  );

  return (
    <>
      <Title>{t(props.title)}</Title>
      <Breadcrumbs title={props.title} links={props.breadcrumbs} hide />
      <EntityForm<CategoryValues>
        initialValues={props.initialValues}
        validationSchema={validationSchema}
        onSubmit={props.onSubmit}
        disabled={props.isRootCategory}
      >
        <TextField
          name="name"
          label="category.name"
          type={TEXT_FIELD_TYPE.TEXT}
          color={TEXT_FIELD_COLOR.PRIMARY}
          variant={TEXT_FIELD_VARIANT.STANDARD}
          disabled={props.isRootCategory}
        />
        <Image
          name="image"
          label="category.image"
          color={TEXT_FIELD_COLOR.PRIMARY}
          dialogTitle="category.add_image"
          disabled={props.isRootCategory}
          dialogButtonActionLabel="button.add"
          uploadSuccessMessage="category.snackbar.upload_success"
          iri={props.categoryId ? props.initialValues.image : undefined}
        />
        <CategoryFilter
          categoryId={props.categoryId}
          parentId={props.parentId}
          disabledCategoryOne={props.isRootCategory || props.isMainCategory}
          removeCategoryThree={true}
        />
      </EntityForm>
    </>
  );
};

export default CategoryForm;
